import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { Fragment } from "react";
import { JsonLd } from "@/components/custom/JsonLd";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { SITE_URL } from "@/constants/siteUrl";
import { findNavItemPath, isNavItem, type NavItemEntry, navItemAbsoluteUrl, navItemDestination, navItemUrl } from "@/lib/contentful/navItem";
import type { PageCrumb } from "@/lib/contentful/pathIndex";
interface Props {
  /** The page's ancestors from `page.parent`, root-first and including the page itself. */
  chain?: PageCrumb[];
  /** Opt-in switch: no breadcrumbs render unless this is linked. Also the trail for pages with no parent. */
  breadcrumbsStructure?: unknown;
  navMenu: NavItemEntry[];
}

/**
 * A crumb ready to render. `linked` and `href` are kept apart on purpose: a nav
 * item with a destination that resolves to no safe href still renders as a link,
 * which is the behaviour this component has always had.
 */
interface Crumb {
  key: string;
  title: string;
  href: string;
  linked: boolean;
  /** Absolute URL for JSON-LD; absent omits `item` from the ListItem. */
  jsonLdId?: string;
}
interface JsonLdItem {
  "@type": "ListItem";
  position: number;
  name: string;
  item?: {
    "@id": string;
    "@type": "WebPage";
  };
}
function buildJsonLdItem(crumb: Crumb, position: number): JsonLdItem {
  const base: JsonLdItem = {
    "@type": "ListItem",
    position,
    name: crumb.title
  };
  if (!crumb.jsonLdId) return base;
  return {
    ...base,
    item: {
      "@id": crumb.jsonLdId,
      "@type": "WebPage"
    }
  };
}

/**
 * Crumbs from the page hierarchy (`page.parent`). A chain of one is a root-level
 * page with nothing to show, so it yields nothing and the nav-derived trail below
 * still applies — that is what keeps pages predating the `parent` field unchanged.
 */
function hierarchyCrumbs(chain: PageCrumb[] | undefined): Crumb[] | null {
  if (!chain || chain.length < 2) return null;
  return chain.map(crumb => ({
    key: crumb.id,
    title: crumb.title,
    href: crumb.path,
    linked: true,
    jsonLdId: `${SITE_URL}${crumb.path}`
  }));
}

/** Crumbs from the nav tree, located by the page's `breadcrumbsStructure` link. */
function navCrumbs(breadcrumbsStructure: unknown, navMenu: NavItemEntry[]): Crumb[] | null {
  if (!isNavItem(breadcrumbsStructure)) return null;
  const found = findNavItemPath(navMenu, breadcrumbsStructure.sys.id);
  const items = found.length > 0 ? found : [breadcrumbsStructure];
  return items.map(item => {
    const linked = navItemDestination(item) !== "";
    return {
      key: item.sys.id,
      title: item.fields.title,
      href: navItemUrl(item),
      linked,
      jsonLdId: linked ? navItemAbsoluteUrl(item) : undefined
    };
  });
}
export function PageBreadcrumbView(t0) {
  const $ = _c(24);
  const {
    chain,
    breadcrumbsStructure,
    navMenu
  } = t0;
  if (!isNavItem(breadcrumbsStructure)) {
    return null;
  }
  let T0;
  let T1;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== breadcrumbsStructure || $[1] !== chain || $[2] !== navMenu) {
    t5 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const crumbs = hierarchyCrumbs(chain) ?? navCrumbs(breadcrumbsStructure, navMenu);
      if (!crumbs) {
        t5 = null;
        break bb0;
      }
      const lastIndex = crumbs.length - 1;
      const jsonLd = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [{
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: {
            "@id": SITE_URL,
            "@type": "WebPage"
          }
        }, ...crumbs.map(_temp)]
      };
      t3 = "p-6 w-full max-w-site-content mx-auto";
      t4 = <JsonLd data={jsonLd} />;
      T1 = Breadcrumb;
      T0 = BreadcrumbList;
      if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = <BreadcrumbItem><BreadcrumbLink asChild={true}><Link href="/">Home</Link></BreadcrumbLink></BreadcrumbItem>;
        $[10] = t1;
      } else {
        t1 = $[10];
      }
      let t6;
      if ($[11] !== lastIndex) {
        t6 = (crumb_0, index_0) => {
          const renderAsPlain = index_0 === lastIndex || !crumb_0.linked;
          return <Fragment key={crumb_0.key}><BreadcrumbSeparator /><BreadcrumbItem>{renderAsPlain ? <BreadcrumbPage>{crumb_0.title}</BreadcrumbPage> : <BreadcrumbLink asChild={true}><Link href={crumb_0.href}>{crumb_0.title}</Link></BreadcrumbLink>}</BreadcrumbItem></Fragment>;
        };
        $[11] = lastIndex;
        $[12] = t6;
      } else {
        t6 = $[12];
      }
      t2 = crumbs.map(t6);
    }
    $[0] = breadcrumbsStructure;
    $[1] = chain;
    $[2] = navMenu;
    $[3] = T0;
    $[4] = T1;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    T0 = $[3];
    T1 = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
  }
  if (t5 !== Symbol.for("react.early_return_sentinel")) {
    return t5;
  }
  let t6;
  if ($[13] !== T0 || $[14] !== t1 || $[15] !== t2) {
    t6 = <T0>{t1}{t2}</T0>;
    $[13] = T0;
    $[14] = t1;
    $[15] = t2;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== T1 || $[18] !== t6) {
    t7 = <T1>{t6}</T1>;
    $[17] = T1;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t3 || $[21] !== t4 || $[22] !== t7) {
    t8 = <div className={t3}>{t4}{t7}</div>;
    $[20] = t3;
    $[21] = t4;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
}
function _temp(crumb, index) {
  return buildJsonLdItem(crumb, index + 2);
}